import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, X, Info } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { formatDate } from "@/utils/formatDate";
import {
  useWeeklyPlans,
  useCreateWeeklyPlan,
  useUpdateWeeklyPlan,
  useDeleteWeeklyPlan,
  useCarryOver,
} from "@/features/tasks/hooks/useWeeklyPlans";
import { useTasks } from "@/features/tasks/hooks/useTasks";
import { useProjects } from "@/features/tasks/hooks/useProjects";
import { TaskDetailPanel } from "@/features/tasks/components/TaskDetailPanel";

function getMondayOf(d: Date): Date {
  const day = new Date(d);
  const diff = day.getDay() === 0 ? -6 : 1 - day.getDay();
  day.setDate(day.getDate() + diff);
  day.setHours(0, 0, 0, 0);
  return day;
}

// Local getters instead of toISOString(): getMondayOf() sets local midnight via setHours(0,0,0,0),
// toISOString() would return the previous day in UTC+2, shifting the whole planner by one day.
function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatWeekLabel(monday: Date): string {
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return `${formatDate(monday)} – ${formatDate(friday)}`;
}

function getWeekNumber(d: Date): number {
  const onejan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7);
}

export function WeeklyPlannerPage() {
  const [currentMonday, setCurrentMonday] = useState<Date>(getMondayOf(new Date()));
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showCarryOver, setShowCarryOver] = useState(false);
  const [filterProject, setFilterProject] = useState("");

  const weekStart = toIso(currentMonday);
  const { data: plans = [], isLoading } = useWeeklyPlans({ project: filterProject || undefined });
  const { data: allTasks = [] } = useTasks();
  const { data: projects = [] } = useProjects();
  const { data: carryOverTasks = [] } = useCarryOver();
  const { mutate: createPlan, isPending: creating } = useCreateWeeklyPlan();
  const { mutate: deletePlan } = useDeleteWeeklyPlan();

  // Plans for the current week
  const weekPlans = plans.filter((p) => p.week_start === weekStart);
  const selectedPlan = weekPlans.find((p) => p.id === selectedPlanId) ?? weekPlans[0] ?? null;
  const { mutate: updatePlan } = useUpdateWeeklyPlan(selectedPlan?.id ?? "");

  // Task search for backlog
  const backlogTasks = allTasks.filter(
    (t) =>
      (taskSearchQuery === "" ||
        t.title.toLowerCase().includes(taskSearchQuery.toLowerCase()))
  );

  // Tasks already in this plan
  const planTaskIds = new Set(selectedPlan?.tasks.map((t) => t.id) ?? []);

  function prevWeek() {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() - 7);
    setCurrentMonday(d);
  }

  function nextWeek() {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() + 7);
    setCurrentMonday(d);
  }

  function goToCurrentWeek() {
    setCurrentMonday(getMondayOf(new Date()));
  }

  function createNewPlan() {
    createPlan(
      { week_start: weekStart, task_ids: [] },
      { onSuccess: (plan) => setSelectedPlanId(plan.id) }
    );
  }

  function addTaskToPlan(taskId: string) {
    if (!selectedPlan) return;
    const currentIds = selectedPlan.tasks.map((t) => t.id);
    if (currentIds.includes(taskId)) return;
    updatePlan({ task_ids: [...currentIds, taskId] });
  }

  function removeTaskFromPlan(taskId: string) {
    if (!selectedPlan) return;
    const newIds = selectedPlan.tasks.map((t) => t.id).filter((id) => id !== taskId);
    updatePlan({ task_ids: newIds });
  }

  function addCarryOverTask(taskId: string) {
    addTaskToPlan(taskId);
  }

  function updateNotes(notes: string) {
    if (!selectedPlan) return;
    updatePlan({ notes });
  }

  const weekLabel = formatWeekLabel(currentMonday);
  const weekNum = getWeekNumber(currentMonday);
  const isCurrentWeek = toIso(getMondayOf(new Date())) === weekStart;

  // Workload summary: task count per assignee in this plan
  const workloadMap: Record<string, { name: string; hours: number; count: number }> = {};
  (selectedPlan?.tasks ?? []).forEach((task) => {
    task.assignees.forEach((a) => {
      if (!workloadMap[a.id]) workloadMap[a.id] = { name: a.full_name, hours: 0, count: 0 };
      workloadMap[a.id].count += 1;
    });
  });
  const workloads = Object.values(workloadMap);

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap flex-shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Planer tygodniowy</h1>
          {isCurrentWeek && (
            <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-[var(--color-primary)] dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
              Bieżący tydzień
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {carryOverTasks.length > 0 && (
            <button
              onClick={() => setShowCarryOver(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50"
            >
              <Info size={14} />
              {carryOverTasks.length} niezakończone z ub. tygodnia
            </button>
          )}
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          >
            <option value="">Wszystkie projekty</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.short_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Week navigation */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          onClick={prevWeek}
          className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center min-w-52">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Tydzień {weekNum} · {weekLabel}
          </p>
        </div>
        <button
          onClick={nextWeek}
          className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <ChevronRight size={16} />
        </button>
        {!isCurrentWeek && (
          <button
            onClick={goToCurrentWeek}
            className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] px-2"
          >
            Wróć do bieżącego
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row gap-4 flex-1 overflow-hidden min-h-0">

        {/* LEFT: Plan content */}
        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : weekPlans.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 gap-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-center py-12">
              <p className="text-gray-400 dark:text-gray-500">Brak planu dla tego tygodnia</p>
              <button
                onClick={createNewPlan}
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
              >
                <Plus size={16} />
                Utwórz plan tygodnia
              </button>
            </div>
          ) : (
            <>
              {/* Plan selector (multiple plans per week possible) */}
              {weekPlans.length > 1 && (
                <div className="flex gap-2">
                  {weekPlans.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        selectedPlan?.id === plan.id
                          ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                          : "border-gray-300 text-gray-600 dark:text-gray-400 hover:border-[var(--color-primary)]"
                      }`}
                    >
                      {plan.project_name ?? "Ogólny"} · {plan.created_by_name}
                    </button>
                  ))}
                  <button
                    onClick={createNewPlan}
                    className="text-xs px-3 py-1 rounded-full border border-dashed border-gray-400 text-gray-500 dark:text-gray-400 hover:border-[var(--color-primary)]"
                  >
                    + Nowy plan
                  </button>
                </div>
              )}

              {selectedPlan && (
                <div className="flex flex-col gap-4 overflow-y-auto flex-1 pr-1">
                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Notatki do tygodnia
                    </label>
                    <textarea
                      defaultValue={selectedPlan.notes}
                      onBlur={(e) => updateNotes(e.target.value)}
                      rows={2}
                      placeholder="Ważne ustalenia, cele tygodnia..."
                      className="w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
                    />
                  </div>

                  {/* Workload bar */}
                  {workloads.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Obciążenie zespołu
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {workloads.map((w) => {
                          const maxCount = Math.max(...workloads.map((x) => x.count), 1);
                          const pct = (w.count / maxCount) * 100;
                          return (
                            <div key={w.name} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 dark:text-gray-400 w-28 truncate">{w.name}</span>
                              <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all bg-blue-400"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 w-16 text-right">
                                {w.count} zad.
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Task list */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Zadania tego tygodnia ({selectedPlan.tasks.length})
                      </p>
                      <button
                        onClick={() =>
                          deletePlan(selectedPlan.id, {
                            onSuccess: () => setSelectedPlanId(null),
                          })
                        }
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        Usuń plan
                      </button>
                    </div>

                    {selectedPlan.tasks.length === 0 ? (
                      <div className="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                        Przeciągnij zadania z backlogu po prawej lub kliknij +
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {selectedPlan.tasks.map((task) => (
                          <div
                            key={task.id}
                            className="flex items-center gap-2 p-2.5 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]/30 group"
                          >
                            <button
                              onClick={() => setSelectedTaskId(task.id)}
                              className="flex-1 text-sm text-left text-gray-800 dark:text-gray-200 hover:text-[var(--color-primary)] truncate"
                            >
                              {task.title}
                            </button>
                            {task.project_name && (
                              <span className="text-xs text-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full">
                                {task.project_name}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {task.done_subtask_count}/{task.subtask_count}
                            </span>
                            <div className="flex -space-x-1">
                              {task.assignees.slice(0, 3).map((a) => (
                                <UserAvatar
                                  key={a.id}
                                  userId={a.id}
                                  fullName={a.full_name}
                                  profilePictureUrl={a.profile_picture_url}
                                  size="sm"
                                  border
                                />
                              ))}
                            </div>
                            <button
                              onClick={() => removeTaskFromPlan(task.id)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500 hover:text-red-500 ml-1"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT: Backlog sidebar */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 flex flex-col gap-3 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 pt-4 lg:pt-0 lg:pl-4">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Backlog — wszystkie zadania
            </p>
            <input
              placeholder="Szukaj zadania..."
              value={taskSearchQuery}
              onChange={(e) => setTaskSearchQuery(e.target.value)}
              className="w-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] mb-2"
            />
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col gap-1.5">
            {backlogTasks.map((task) => {
              const inPlan = planTaskIds.has(task.id);
              return (
                <div
                  key={task.id}
                  className={`flex items-center gap-2 p-2 rounded-lg border text-sm transition-colors ${
                    inPlan
                      ? "border-green-200 bg-green-50 text-gray-400 dark:text-gray-500"
                      : "border-gray-200 dark:border-gray-700 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]/30 cursor-pointer"
                  }`}
                  onClick={() => !inPlan && selectedPlan && addTaskToPlan(task.id)}
                >
                  <span className="flex-1 truncate">{task.title}</span>
                  {inPlan ? (
                    <span className="text-xs text-green-600">✓</span>
                  ) : selectedPlan ? (
                    <Plus size={13} className="text-[var(--color-primary)] flex-shrink-0" />
                  ) : null}
                </div>
              );
            })}
            {backlogTasks.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">Brak wyników</p>
            )}
          </div>
        </div>
      </div>

      {/* Carry-over modal */}
      {showCarryOver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Niezakończone zadania z poprzedniego tygodnia</h3>
              <button onClick={() => setShowCarryOver(false)}>
                <X size={18} className="text-gray-400 dark:text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 flex flex-col gap-2 max-h-72 overflow-y-auto">
              {carryOverTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between py-1"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{task.title}</span>
                  <button
                    onClick={() => addCarryOverTask(task.id)}
                    disabled={planTaskIds.has(task.id)}
                    className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:text-gray-400 ml-3 flex-shrink-0"
                  >
                    {planTaskIds.has(task.id) ? "Dodane ✓" : "+ Przenieś"}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end px-6 py-3 border-t border-gray-100">
              <button
                onClick={() => setShowCarryOver(false)}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task detail panel */}
      {selectedTaskId && (
        <TaskDetailPanel
          taskId={selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
        />
      )}
    </div>
  );
}
